import React from 'react';
import { TDR } from 'tdr-common';
import { useRestaurantBasePolicies } from '../../api/getRestaurantBasePolicies';
import { useRestaurantTaxPolicies } from '../../api/getRestaurantTaxPolicies';
import { InvoiceItem } from '../checkout/InvoiceItem';
import {
	getDepositLineItems,
	getTableSelectionFeeItem,
	sumDepositLineItems,
	getTaxBreakdown
} from '../checkout/util';
import { DepositInfo } from '../dialog/info-dialog/DepositInfo';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '../skeleton/Skeleton';

type OrderSummaryProps = {
	restaurant: TDR.Restaurant;
	invoice: TDR.Invoice | undefined;
	tableId: string;
	guests: number;
};

// Skeleton component for invoice items
const InvoiceItemSkeleton = ({ titleWidth = '120px', amountWidth = '60px' }: { titleWidth?: string; amountWidth?: string }) => (
	<>
		<li className='InvoiceItem'>
			<div className='InvoiceItem__Description'>
				<div className='InvoiceItem__Title'>
					<Skeleton height='20px' width={titleWidth} />
				</div>
			</div>
			<div className='InvoiceItem__Amount'>
				<Skeleton height='20px' width={amountWidth} />
			</div>
		</li>
		<div style={{ height: '1px', backgroundColor: '#E5E7EB', margin: '12px 0' }} />
	</>
);

const OrderSummarySkeleton = () => (
	<ul className='OrderSummary'>
		<InvoiceItemSkeleton titleWidth='160px' amountWidth='70px' />
		<InvoiceItemSkeleton titleWidth='130px' amountWidth='70px' />
		<InvoiceItemSkeleton titleWidth='60px' amountWidth='60px' />
		<InvoiceItemSkeleton titleWidth='80px' amountWidth='65px' />
		<InvoiceItemSkeleton titleWidth='100px' amountWidth='75px' />
		<InvoiceItemSkeleton titleWidth='120px' amountWidth='80px' />
	</ul>
);

export const OrderSummary = ({
	restaurant,
	invoice,
	guests
}: OrderSummaryProps) => {
	const { t } = useTranslation();

	const { lineItems = [], deposit = 0 } = invoice || {};

	const { data: taxPolicies = [] } = useRestaurantTaxPolicies(restaurant);
	const { data: basePolicies = [] } = useRestaurantBasePolicies(restaurant?.id);

	const DEFAULT_DEPOSIT_LABEL = t('titles.deposit');
	const tableFeeItem = getTableSelectionFeeItem(lineItems);
	const depositLineItems = getDepositLineItems(lineItems, DEFAULT_DEPOSIT_LABEL, guests, basePolicies);
	const depositAmount = Math.max(sumDepositLineItems(lineItems), deposit);
	const taxBreakdown = getTaxBreakdown(invoice, taxPolicies);
	const newBookingFee = tableFeeItem.total;
	const previousBookingFee = invoice?.modifiedFrom?.subtotal || 0;
	const previousPayment = invoice?.modifiedFrom?.total || 0;
	const previousDeposit = invoice?.modifiedFrom?.deposit || 0;

	if (!invoice) {
		return <OrderSummarySkeleton />;
	}

	return (
		<>
			<ul className='OrderSummary'>
				<InvoiceItem
					title={t('titles.previous_booking_fee', { defaultValue: 'Previous Booking Fee' })}
					amount={previousBookingFee}
					currency={restaurant.currency}
					isDivided={false}
					variant={'line-through'}
				/>

				<InvoiceItem
					title={t('titles.new_booking_fee', { defaultValue: 'New Booking Fee' })}
					amount={newBookingFee}
					currency={restaurant.currency}
				/>

				{/* Individual Taxes */}
				{taxBreakdown.map((tax, index) => (
					<InvoiceItem
						key={tax.taxId || index}
						title={`${tax.name} (${tax.percentage.toFixed(tax.percentage % 1 === 0 ? 0 : 3)}%)`}
						amount={tax.amount}
						currency={restaurant.currency}
						isDivided={false}
					/>
				))}

				{/* Deposit */}

				{previousDeposit !== depositAmount && (
					<InvoiceItem
						title={t('titles.previous_deposit', { defaultValue: 'Previous Deposit' })}
						amount={previousDeposit}
						currency={restaurant.currency}
						variant={'line-through'}
						isDivided={false}
					/>
				)}

				{depositLineItems.length > 0 ? (
					depositLineItems.map((depositItem, index) => (
						<InvoiceItem
							key={index}
							title={depositItem.label}
							iconAfter={depositItem.label === DEFAULT_DEPOSIT_LABEL ? <DepositInfo restaurant={restaurant} invoice={invoice} /> : undefined}
							amount={depositItem.amount}
							currency={restaurant.currency}
							isDivided={index === depositLineItems.length - 1}
						/>
					))
				) : depositAmount > 0 ? (
					<InvoiceItem
						title={DEFAULT_DEPOSIT_LABEL}
						iconAfter={<DepositInfo restaurant={restaurant} invoice={invoice} />}
						amount={depositAmount}
						currency={restaurant.currency}
					/>
				) : null}

				{/* Total amount */}
				<InvoiceItem
					title={t('titles.total', { defaultValue: 'Total' })}
					amount={invoice?.total || 0}
					currency={restaurant.currency}
					variant={'bold'}
				/>

				{/* Show previous amount paid */}
				<InvoiceItem
					title={t('titles.amount_paid')}
					amount={previousPayment}
					currency={restaurant.currency}
					isDivided={false}
					variant={'light'}
				/>

				{/* Outstanding amount */}
				<InvoiceItem
					title={(invoice?.payable || 0) > 0 ? t('titles.amount_due', { defaultValue: 'Amount Due' }) : t('titles.amount_to_be_refunded', { defaultValue: 'Amount to be Refunded' })}
					amount={Math.abs(invoice?.payable || 0)}
					currency={restaurant.currency}
					isDivided={false}
					variant={'bold'}
				/>
			</ul>
		</>
	);
};
